// src/api/employee/updatePersonalInfo.ts

import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const REGISTER_ADMIN_BASE_URL = `${API_BASE_PATH}/register/admin`;
const URL_EMPLOYEE = `${API_BASE_PATH}/register`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes if any
  }

  return detail || details || message;
};

// ✅ Enhanced error handling for updateEmployee
export const updateEmployee = async (
  employeeId: string,
  data: FormData,
  _token?: string | null
): Promise<void> => {
  try {
    const response = await axios.put(
      `${REGISTER_ADMIN_BASE_URL}/update/${employeeId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + _token,
        },
      }
    );

    // Don't show success toast here - handled in component
    return response.data;
  } catch (error: any) {
    // ✅ Check for DATABASE_ERROR and handle gracefully
    if (error?.response?.data?.code === "DATABASE_ERROR") {
      // Re-throw with a specific flag to identify database constraint errors
      throw {
        ...error,
        message: "DATABASE_ERROR",
        userFriendlyMessage: "Please fill all required fields correctly."
      };
    }
    
    console.error("Error updating employee:", error);
    const message = extractErrorMessage(error);
    throw { message };
  }
};

export interface PersonalInfoPayload {
  date_of_birth: string;
  aadhar_no: number;
  passport_no: string;
  religion: string;
  number_of_children: number;
  pan_card: string;
  spouse_name: string;
  blood_group: string;
  nationality: string;
  marital_status: string;
  passport_exp_date: string; // Format: YYYY-MM-DD
}

// ✅ updatePersonalInfo with token
export const updatePersonalInfo = async (
  employeeId: string,
  payload: PersonalInfoPayload,
  _token?: string | null
): Promise<boolean> => {
  const url = `${URL_EMPLOYEE}/admin/employee/${employeeId}/personal_info`;

  const formData = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    formData.append(key, value.toString());
  });

  try {
    const response = await axios.put(url, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Authorization: "Bearer " + _token,
      },
    });

    if (response.status === 200) {
      toast.success("Personal info updated successfully");
      return true;
    } else {
      console.debug("Unexpected response:", response);
      toast.error("Failed to update personal info");
      return false;
    }
  } catch (error: any) {
    // ✅ Handle DATABASE_ERROR for personal info as well
    if (error?.response?.data?.code === "DATABASE_ERROR") {
      toast.warning("Please fill all required fields correctly.");
      return false;
    } 
    const message = extractErrorMessage(error);
    console.error("Error updating personal info:", error);
    toast.error(message);
    return false;
  }
};